import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSdkViewerScope } from '../../provider/SdkRuntimeContext.js';
import { giftEntityKey, readGiftEntity } from './gift-entity.js';
import { createSubscriberQueryFn, getSubscriberQueryMetaOptions } from './subscriber-query-fn.js';
/**
 * Makes a mounted gift surface read its mutable fields from the one canonical
 * network/PDA entity. The source query remains responsible only for collection
 * membership and presentation-specific fields.
 */
export function useGiftEntityRows(rows, network) {
    const queryClient = useQueryClient();
    const viewerScope = useSdkViewerScope() || 'anonymous';
    const giftPdas = uniqueGiftPdas(rows);
    const entityResults = useQueries({
        queries: giftPdas.map((giftPda) => {
            const queryKey = giftEntityKey(network, giftPda);
            return {
                queryKey,
                queryFn: createSubscriberQueryFn(queryClient, queryKey),
                ...getSubscriberQueryMetaOptions(queryClient, queryKey),
                enabled: false,
                staleTime: Number.POSITIVE_INFINITY,
            };
        }),
    });
    if (rows === undefined)
        return undefined;
    const entityByPda = new Map();
    for (let index = 0; index < giftPdas.length; index += 1) {
        const giftPda = giftPdas[index];
        const entity = entityResults[index]?.data;
        if (giftPda !== undefined && entity !== undefined)
            entityByPda.set(giftPda, entity);
    }
    return rows.map((row) => materializeGiftRow(queryClient, asGiftRow(row), network, viewerScope, entityByPda));
}
/** Convenience form for a permalink/detail response. */
export function useGiftEntityRow(row, network) {
    const rows = useMemo(() => (row === undefined ? undefined : [row]), [row]);
    const materializedRows = useGiftEntityRows(rows, network);
    return materializedRows?.[0];
}
function materializeGiftRow(queryClient, row, network, viewerScope, entityByPda) {
    const giftPda = giftPdaOf(row);
    if (giftPda === undefined)
        return row;
    const canonical = queryClient.getQueryData(giftEntityKey(network, giftPda));
    const entity = canonical === undefined
        ? entityByPda.get(giftPda)
        : readGiftEntity(queryClient, { network, viewerScope }, giftPda);
    return entity === undefined ? row : { ...row, ...entity };
}
function uniqueGiftPdas(rows) {
    if (rows === undefined)
        return [];
    const giftPdas = new Set();
    for (const row of rows) {
        const giftPda = giftPdaOf(asGiftRow(row));
        if (giftPda !== undefined)
            giftPdas.add(giftPda);
    }
    return [...giftPdas];
}
function giftPdaOf(row) {
    const giftPda = row.giftPda;
    return typeof giftPda === 'string' && giftPda.length > 0 ? giftPda : undefined;
}
function asGiftRow(row) {
    return row;
}
//# sourceMappingURL=use-gift-entity.js.map